<template>
  <div class="field-selector">
    <!-- 搜索区域 -->
    <div class="search-section">
      <div class="search-box">
        <span class="search-icon">🔍</span>
        <input
          v-model="searchKeyword"
          type="text"
          placeholder="搜索字段名称..."
          class="search-input"
          @input="handleSearch"
        />
        <button 
          v-if="searchKeyword"
          @click="clearSearch"
          class="clear-btn"
        >
          ✕
        </button>
      </div>
      
      <!-- 移除快速操作按钮 -->
    </div>

    <!-- 字段分类 -->
    <div class="field-categories">
      <div 
        v-for="(category, categoryName) in filteredCategories" 
        :key="categoryName"
        class="field-category"
      >
        <!-- 分类头部 -->
        <div 
          class="category-header"
          @click="toggleCategory(categoryName)"
          :class="{ expanded: category.expanded }"
        >
          <div class="category-info">
            <span class="expand-icon">
              {{ category.expanded ? '📂' : '📁' }}
            </span>
            <span class="category-title">{{ category.title }}</span>
            <span class="field-count">({{ getFieldCount(category) }})</span>
          </div>
          <div class="category-actions">
            <span class="selected-count">
              已选: {{ getSelectedCount(category) }}
            </span>
          </div>
        </div>

        <!-- 分类内容 -->
        <Transition name="slide">
          <div v-if="category.expanded" class="category-content">
            <div 
              v-for="field in getFilteredFields(category.fields)" 
              :key="field.id"
              class="field-item"
              :class="{ 
                selected: field.selected,
                'has-subfields': field.subFields 
              }"
            >
              <!-- 主字段 -->
              <div class="field-main" @click="!field.subFields && addFieldToCanvas(field)">
                <div class="field-row">
                  <div class="field-checkbox">
                    <input
                      type="checkbox"
                      :checked="field.selected"
                      @change="toggleFieldSelection(categoryName, field.id)"
                      @click.stop
                    />
                  </div>
                  
                  <div class="field-info">
                    <div class="field-name">{{ field.name }}</div>
                    <div v-if="field.description" class="field-desc">
                      {{ field.description }}
                    </div>
                  </div>
                  
                  <div class="field-actions">
                    <button
                      v-if="field.subFields"
                      @click.stop="toggleSubFields(categoryName, field.id)"
                      class="expand-btn"
                      :class="{ expanded: field.expanded }"
                    >
                      {{ field.expanded ? '▲' : '▼' }}
                    </button>
                  </div>
                </div>
                
                <!-- 编辑器行 - 仅在选中时显示 -->
                <div v-if="field.selected" class="field-editor-row">
                  <div class="field-editor">
                    <input
                      v-if="field.editable"
                      :value="field.displayName || ''"
                      type="text"
                      class="display-name-input"
                      :placeholder="field.name"
                      @click.stop
                      @input="updateFieldDisplayName(categoryName, field.id, $event.target.value)"
                    />
                    <span v-else class="display-name">
                      显示名: {{ field.displayName || field.name }}
                    </span>
                  </div>
                </div>
              </div>

              <!-- 子字段 -->
              <Transition name="slide">
                <div v-if="field.expanded && field.subFields" class="sub-fields">
                  <div 
                    v-for="subField in getFilteredFields(field.subFields)" 
                    :key="subField.id"
                    class="sub-field-item"
                    :class="{ selected: subField.selected }"
                    @click="addFieldToCanvas(subField)"
                  >
                    <div class="field-checkbox">
                      <input
                        type="checkbox"
                        :checked="subField.selected"
                        @change="toggleFieldSelection(categoryName, field.id, subField.id)"
                        @click.stop
                      />
                    </div>
                    
                    <div class="field-info">
                      <div class="field-name">{{ subField.name }}</div>
                    </div>
                    
                    <div class="field-editor">
                      <input
                        v-if="subField.editable && subField.selected"
                        :value="subField.displayName || ''"
                        type="text"
                        class="display-name-input"
                        :placeholder="subField.name"
                        @click.stop
                        @input="updateFieldDisplayName(categoryName, field.id, $event.target.value, subField.id)"
                      />
                      <span v-else class="display-name">
                        {{ subField.displayName || subField.name }}
                      </span>
                    </div>
                    
                    <div class="field-actions">
                      <!-- 移除加号按钮 -->
                    </div>
                  </div>
                </div>
              </Transition>
            </div>
          </div>
        </Transition>
      </div>
    </div>
    
    <!-- 空状态 -->
    <div v-if="Object.keys(filteredCategories).length === 0" class="empty-state">
      <div class="empty-icon">🔍</div>
      <div class="empty-text">未找到匹配的字段</div>
      <button @click="clearSearch" class="clear-search-btn">清除搜索</button>
    </div>
  </div>
</template>

<script>
import { computed } from 'vue'
import { useStore } from 'vuex'
import { FieldService } from '@/services/fieldService'

export default {
  name: 'FieldSelector',
  emits: ['add-field'],
  
  setup(props, { emit }) {
    const store = useStore()
    
    // 响应式数据
    const searchKeyword = computed({
      get: () => store.state.fields.searchKeyword,
      set: (value) => store.dispatch('fields/setSearchKeyword', value)
    })
    
    const fieldCategories = computed(() => store.state.fields.fieldCategories)
    
    // 过滤后的分类
    const filteredCategories = computed(() => {
      if (!searchKeyword.value) {
        return fieldCategories.value
      }
      
      return FieldService.searchFields(fieldCategories.value, searchKeyword.value)
    })
    
    // 方法
    const handleSearch = (event) => {
      searchKeyword.value = event.target.value
    }
    
    const clearSearch = () => {
      searchKeyword.value = ''
    }
    
    const toggleCategory = (categoryName) => {
      store.dispatch('fields/toggleCategory', categoryName)
    }
    
    const toggleSubFields = (categoryName, fieldId) => {
      store.dispatch('fields/toggleSubFields', { categoryName, fieldId })
    }
    
    const toggleFieldSelection = (categoryName, fieldId, subFieldId = null) => {
      // 获取当前字段状态
      const category = fieldCategories.value[categoryName]
      if (!category) return
      
      let field, currentlySelected
      
      if (subFieldId) {
        // 处理子字段
        const parentField = category.fields.find(f => f.id === fieldId)
        field = parentField?.subFields?.find(sf => sf.id === subFieldId)
        currentlySelected = field?.selected || false
      } else {
        // 处理主字段
        field = category.fields.find(f => f.id === fieldId)
        currentlySelected = field?.selected || false
      }
      
      if (!field) return
      
      // 先更新字段状态
      store.dispatch('fields/toggleFieldSelection', { 
        categoryName, 
        fieldId, 
        subFieldId 
      })
      
      // 根据新状态添加或删除元素
      if (!currentlySelected) {
        // 字段被选中，添加到画布
        emit('add-field', field)
      } else {
        // 字段被取消选中，从画布删除
        removeFieldFromCanvas(field)
      }
    }
    
    const updateFieldDisplayName = (categoryName, fieldId, displayName, subFieldId = null) => {
      store.dispatch('fields/updateFieldDisplayName', {
        categoryName,
        fieldId,
        subFieldId,
        displayName
      })
    }
    
    const addFieldToCanvas = (field) => {
      if (!field.selected) {
        // 如果字段未选中，先选中它
        const categoryName = getCategoryName(field)
        const parentField = getParentField(field)
        
        if (parentField) {
          toggleFieldSelection(categoryName, parentField.id, field.id)
        } else {
          toggleFieldSelection(categoryName, field.id)
        }
      }
      
      emit('add-field', field)
    }
    
    const removeFieldFromCanvas = (field) => {
      // 发送删除字段事件
      emit('remove-field', field)
    }
    
    const getCategoryName = (field) => {
      for (const [categoryName, category] of Object.entries(fieldCategories.value)) {
        const found = category.fields.find(f => 
          f.id === field.id || 
          (f.subFields && f.subFields.find(sf => sf.id === field.id))
        )
        if (found) return categoryName
      }
      return null
    }
    
    const getParentField = (field) => {
      for (const category of Object.values(fieldCategories.value)) {
        for (const parentField of category.fields) {
          if (parentField.subFields && parentField.subFields.find(sf => sf.id === field.id)) {
            return parentField
          }
        }
      }
      return null
    }
    
    const getFilteredFields = (fields) => {
      if (!searchKeyword.value) return fields
      
      return fields.filter(field => 
        field.name.toLowerCase().includes(searchKeyword.value.toLowerCase()) ||
        (field.displayName && field.displayName.toLowerCase().includes(searchKeyword.value.toLowerCase()))
      )
    }
    
    const getFieldCount = (category) => {
      let count = category.fields.length
      category.fields.forEach(field => {
        if (field.subFields) {
          count += field.subFields.length
        }
      })
      return count
    }
    
    const getSelectedCount = (category) => {
      let count = 0
      category.fields.forEach(field => {
        if (field.selected) count++
        if (field.subFields) {
          field.subFields.forEach(subField => {
            if (subField.selected) count++
          })
        }
      })
      return count
    }
    
    // 移除了全选、清空、展开全部功能
    
    return {
      searchKeyword,
      filteredCategories,
      handleSearch,
      clearSearch,
      toggleCategory,
      toggleSubFields,
      toggleFieldSelection,
      updateFieldDisplayName,
      addFieldToCanvas,
      removeFieldFromCanvas,
      getFilteredFields,
      getFieldCount,
      getSelectedCount
    }
  }
}
</script>

<style scoped>
.field-selector {
  height: 100%;
  display: flex;
  flex-direction: column;
  background: #fff;
}

/* 搜索区域 */
.search-section {
  padding: 16px;
  border-bottom: 1px solid #e9ecef;
  background: #f8f9fa;
}

.search-box {
  position: relative;
  margin-bottom: 12px;
}

.search-icon {
  position: absolute;
  left: 12px;
  top: 50%;
  transform: translateY(-50%);
  font-size: 16px;
  color: #6c757d;
}

.search-input {
  width: 100%;
  padding: 10px 40px 10px 40px;
  border: 2px solid #dee2e6;
  border-radius: 8px;
  font-size: 14px;
  transition: border-color 0.2s;
}

.search-input:focus {
  outline: none;
  border-color: #28a745;
  box-shadow: 0 0 0 3px rgba(40, 167, 69, 0.1);
}

.clear-btn {
  position: absolute;
  right: 8px;
  top: 50%;
  transform: translateY(-50%);
  background: none;
  border: none;
  font-size: 16px;
  color: #6c757d;
  cursor: pointer;
  padding: 4px;
  border-radius: 50%;
}

.clear-btn:hover {
  background: #e9ecef;
  color: #495057;
}

/* 移除了快速操作按钮样式 */

/* 字段分类 */
.field-categories {
  flex: 1;
  overflow-y: auto;
  padding: 8px;
}

.field-category {
  margin-bottom: 8px;
  border: 1px solid #dee2e6;
  border-radius: 8px;
  overflow: hidden;
  background: #fff;
}

.category-header {
  display: flex;
  justify-content: space-between;
  align-items: center;
  padding: 12px 16px;
  background: linear-gradient(135deg, #f8f9fa 0%, #e9ecef 100%);
  cursor: pointer;
  transition: all 0.2s;
  border-bottom: 1px solid transparent;
}

.category-header:hover {
  background: linear-gradient(135deg, #e9ecef 0%, #dee2e6 100%);
}

.category-header.expanded {
  border-bottom-color: #dee2e6;
}

.category-info {
  display: flex;
  align-items: center;
  gap: 8px;
}

.expand-icon {
  font-size: 16px;
}

.category-title {
  font-weight: 600;
  color: #495057;
}

.field-count {
  font-size: 12px;
  color: #6c757d;
}

.category-actions {
  display: flex;
  align-items: center;
  gap: 12px;
}

.selected-count {
  font-size: 12px;
  color: #28a745;
  font-weight: 500;
}

.category-content {
  background: #fff;
}

/* 字段项 */
.field-item {
  border-bottom: 1px solid #f1f3f5;
}

.field-item:last-child {
  border-bottom: none;
}

.field-item.selected {
  background: rgba(40, 167, 69, 0.05);
}

.field-main {
  padding: 8px 12px;
  cursor: pointer;
  transition: background-color 0.2s;
}

.field-row {
  display: flex;
  align-items: center;
  gap: 8px;
  width: 100%;
}

.field-editor-row {
  margin-top: 8px;
  padding-left: 32px;
}

.field-main:hover {
  background: rgba(0, 123, 255, 0.05);
}

.field-checkbox {
  display: flex;
  justify-content: center;
  flex-shrink: 0;
  width: 24px;
}

.field-checkbox input[type="checkbox"] {
  width: 16px;
  height: 16px;
  cursor: pointer;
}

.field-info {
  flex: 1;
  min-width: 0;
}

.field-name {
  font-weight: 500;
  color: #495057;
  font-size: 13px;
  line-height: 1.3;
}

.field-desc {
  font-size: 12px;
  color: #6c757d;
  margin-top: 2px;
  line-height: 1.3;
}

.field-editor {
  display: flex;
  align-items: center;
  width: 100%;
}

.display-name-input {
  width: 100%;
  padding: 6px 10px;
  border: 1px solid #dee2e6;
  border-radius: 4px;
  font-size: 12px;
  background: #fff;
}

.display-name-input:focus {
  outline: none;
  border-color: #28a745;
  box-shadow: 0 0 0 2px rgba(40, 167, 69, 0.1);
}

.display-name {
  font-size: 12px;
  color: #6c757d;
  padding: 6px 10px;
  word-break: break-all;
  white-space: normal;
  line-height: 1.2;
  width: 100%;
  text-align: left;
}

.field-actions {
  display: flex;
  gap: 4px;
  justify-content: center;
  flex-shrink: 0;
  min-width: 32px;
}

.expand-btn {
  padding: 4px 8px;
  border: 1px solid #dee2e6;
  background: #fff;
  border-radius: 4px;
  cursor: pointer;
  font-size: 12px;
  transition: all 0.2s;
}

.expand-btn:hover {
  background: #e9ecef;
  border-color: #adb5bd;
}

/* 子字段 */
.sub-fields {
  background: #f8f9fa;
  border-top: 1px solid #e9ecef;
}

.sub-field-item {
  display: grid;
  grid-template-columns: 40px 1fr 160px 60px;
  align-items: center;
  padding: 10px 16px 10px 32px;
  gap: 12px;
  cursor: pointer;
  transition: background-color 0.2s;
  border-bottom: 1px solid #e9ecef;
}

.sub-field-item:hover {
  background: rgba(0, 123, 255, 0.05);
}

.sub-field-item.selected {
  background: rgba(40, 167, 69, 0.05);
}

.sub-field-item:last-child {
  border-bottom: none;
}

/* 空状态 */
.empty-state {
  flex: 1;
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  padding: 40px 20px;
  text-align: center;
}

.empty-icon {
  font-size: 48px;
  margin-bottom: 16px;
  opacity: 0.5;
}

.empty-text {
  font-size: 16px;
  color: #6c757d;
  margin-bottom: 20px;
}

.clear-search-btn {
  padding: 8px 16px;
  background: #28a745;
  color: #fff;
  border: none;
  border-radius: 6px;
  cursor: pointer;
  font-size: 14px;
}

.clear-search-btn:hover {
  background: #218838;
}

/* 动画效果 */
.slide-enter-active,
.slide-leave-active {
  transition: all 0.3s ease;
  overflow: hidden;
}

.slide-enter-from,
.slide-leave-to {
  max-height: 0;
  opacity: 0;
}

.slide-enter-to,
.slide-leave-from {
  max-height: 1000px;
  opacity: 1;
}

/* 字段名称优化 */
.field-name {
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
}

.field-desc {
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
}

/* 响应式设计 */
@media (max-width: 768px) {
  .field-row {
    flex-direction: column;
    align-items: stretch;
    gap: 4px;
  }
  
  .field-checkbox {
    width: auto;
    justify-content: flex-start;
  }
  
  .field-info {
    margin-left: 0;
  }
  
  .field-actions {
    justify-content: flex-end;
    min-width: auto;
  }
  
  .field-editor-row {
    padding-left: 0;
    margin-top: 4px;
  }
}
</style>
